import { type AxePurchaseState, type v2 } from '@deque/billing-service-client';
import {
  getControllingProductSubscription,
  listUserProductAccess
} from '@deque/billing-utils';
import {
  ACTIVE_STATES,
  INTEGRATION_PRODUCT_SLUGS,
  type ProductSlugs,
  SupportedIntegrationProductSlugs
} from '~/common/constants';
import { useAuthContext } from '~/common/contexts/auth';
import { useEnterprises } from '~/common/contexts/enterprises';
import { useFeatureFlags } from '~/common/contexts/featureFlags';
import { toError } from '~/common/utils/toError';

export interface UseIntegrationsState {
  enabledIntegrationProductSlugs: SupportedIntegrationProductSlugs[];
  userHasAccessToIntegrationProductSlugs: SupportedIntegrationProductSlugs[];
  canAccessIntegrationConfiguration: boolean;
  activeEnterprise: v2.Enterprise | null;
  isEnterpriseAdmin: boolean;
  loading: boolean;
  error: Error | null;
}

const createEarlyReturn = (
  error: Error | string | null,
  loading: boolean
): UseIntegrationsState => ({
  enabledIntegrationProductSlugs: [],
  userHasAccessToIntegrationProductSlugs: [],
  canAccessIntegrationConfiguration: false,
  activeEnterprise: null,
  isEnterpriseAdmin: false,
  loading: loading,
  error: toError(error)
});

export const useIntegrations = (): UseIntegrationsState => {
  const {
    billingUser,
    loading: isUserLoading,
    error: userLoadError
  } = useAuthContext();

  const {
    loading: areFeatureFlagsLoading,
    loadError: featureFlagsLoadError,
    featureFlags
  } = useFeatureFlags();

  const {
    activeEnterprise,
    isAdmin: isEnterpriseAdmin,
    loading: areEnterprisesLoading,
    error: enterpriseLoadError
  } = useEnterprises();

  // Return early if loading or in case of an error
  if (areFeatureFlagsLoading || areEnterprisesLoading || isUserLoading) {
    return createEarlyReturn(null, true);
  }
  if (featureFlagsLoadError) {
    return createEarlyReturn(featureFlagsLoadError, false);
  }
  if (enterpriseLoadError) {
    return createEarlyReturn(enterpriseLoadError, false);
  }
  if (userLoadError) {
    return createEarlyReturn(userLoadError, false);
  }

  if (!activeEnterprise) {
    return createEarlyReturn(null, false);
  }

  const hasCustomIntegrationV1 = featureFlags.some(
    flag => flag.id === 'custom_integration_v1' && flag.state === true
  );

  const featureEnabledIntegrationProductSlugs: SupportedIntegrationProductSlugs[] =
    INTEGRATION_PRODUCT_SLUGS.filter(slug =>
      slug === SupportedIntegrationProductSlugs.customIntegration
        ? hasCustomIntegrationV1
        : true
    );

  const enabledIntegrationProductSlugs: SupportedIntegrationProductSlugs[] =
    featureEnabledIntegrationProductSlugs.filter(slug => {
      return isEnterpriseAdmin
        ? ACTIVE_STATES.includes(
            getControllingProductSubscription({
              entity: activeEnterprise,
              productSlug: slug as unknown as ProductSlugs,
              includeEnterprises: 'active-only'
            })?.purchase_state as AxePurchaseState
          )
        : billingUser &&
            ACTIVE_STATES.includes(
              listUserProductAccess(billingUser, 'active-only')[
                slug
              ] as AxePurchaseState
            );
    });
  const userHasAccessToIntegrationProductSlugs: SupportedIntegrationProductSlugs[] =
    featureEnabledIntegrationProductSlugs.filter(slug => {
      return (
        billingUser &&
        ACTIVE_STATES.includes(
          listUserProductAccess(billingUser)[slug] as AxePurchaseState
        )
      );
    });
  const canAccessIntegrationConfiguration =
    enabledIntegrationProductSlugs.length > 0;

  return {
    enabledIntegrationProductSlugs,
    userHasAccessToIntegrationProductSlugs,
    canAccessIntegrationConfiguration,
    activeEnterprise,
    isEnterpriseAdmin,
    loading: false,
    error: null
  };
};
